import type { FunctionComponent } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';

import type { MethodType } from '@endeavour/payment-integration';

import logo from '../../../../assets/logo/logo--full-vertical.svg';
import logoNavy from '../../../../assets/logo/LOGO_WHITE.svg';
import { DisplayType } from '../../../shared/components/form-builder/form-builder';
import type { IStep } from '../../../shared/components/form-flow/form-flow';
import { FormFlow } from '../../../shared/components/form-flow/form-flow';
import { ImageLogo } from '../../../shared/components/image-logo/image-logo.';
import { Loader } from '../../../shared/components/loader/loader';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { capitalize } from '../../../shared/utils/strings';
import { Cashier } from '../../components/cashier/cashier';
import { Error } from '../../components/error/error';
import { PaymentMethodSwitcher } from '../../components/payment-method-switcher/payment-method-switcher';
import { PaymentRedirect } from '../../components/payment-redirect/payment-redirect';
import { useDetailsForm } from '../../hooks/use-details-form';
import { usePaymentProviders } from '../../hooks/use-payment-providers';
import { useProviderIntegration } from '../../util/provider-context';

import styles from './details.module.scss';

const style = createStyleHelper(styles, 'details');

export interface IDetailsProps {
    methodType: MethodType;
}

export const Details: FunctionComponent<IDetailsProps> = ({ methodType }) => {
    const intl = useIntl();
    const { method, accountId, integrationId } = useParams();
    const integration = useProviderIntegration();
    const auth = useAuth();
    const userProfileId = auth?.user?.profile?.user_profile_id as string;
    const { fetching, paymentList } = usePaymentProviders({
        type: methodType,
        slug: method,
    });
    const provider = paymentList?.[0];
    const providerId = provider?.providerId ?? '';
    const paymentMethod = useMemo(
        () => paymentList.find((item) => item.providerId === providerId),
        [paymentList, providerId],
    );
    const isDesktop = useDesktop();
    const form = useDetailsForm({
        providerId,
        accountId: accountId,
        type: methodType,
        integrationId: integrationId ?? '',
        paymentMethod,
    });

    const execute = useCallback(
        (formData?: Record<string, string | number | boolean> & { amount: number }) => {
            if (!userProfileId || !integrationId) {
                return;
            }

            return integration.processPayment({
                configuration: {
                    userProfileId: userProfileId,
                },
                integrationId,
                type: methodType,
                providerId,
                accountId,
                formData,
            });
        },
        [accountId, integrationId, integration, providerId, methodType, userProfileId],
    );

    const stepData = useMemo(() => {
        if (!form) {
            return;
        }

        const processStep: IStep = {
            name: 'process',
            components: ({ additionalData }) => {
                return [
                    {
                        name: 'response',
                        type: DisplayType.COMPONENT,
                        component: additionalData.success ? PaymentRedirect : Error,
                        props: {
                            type: methodType,
                            error: !additionalData.success
                                ? additionalData.error ?? 'UNKNOWN_ERROR'
                                : undefined,
                            transactionId: additionalData.transactionId,
                            responseModel: additionalData,
                        },
                    },
                ];
            },
        };

        if (form.skip) {
            return [
                {
                    ...processStep,
                    async onBeforeEnterStep() {
                        try {
                            return execute();
                        } catch (e) {
                            return { success: false, error: 'UNKNOWN_PROVIDER_ERROR_1' };
                        }
                    },
                },
            ] as IStep[];
        }

        return [
            {
                name: 'method-details',
                schema: form.schema,
                components: form.components,
                async onBeforeLeaveStep(formData) {
                    try {
                        const response = await execute(formData);
                        return { additionalData: response };
                    } catch (e) {
                        return {
                            additionalData: { success: false, error: 'UNKNOWN_PROVIDER_ERROR_2' },
                        };
                    }
                },
            },
            processStep,
        ] as IStep[];
    }, [execute, form, methodType]);

    const formResetHandler = useCallback(
        (original: Record<string, unknown>) => {
            return form?.defaultValues ?? original;
        },
        [form?.defaultValues],
    );

    const isFormLoading = (!form || !form.components) && !form?.skip;
    if (fetching || isFormLoading || !stepData || !provider) {
        return <Loader />;
    }

    return (
        <Cashier
            title={intl.formatMessage({
                id: `cashier.details.${methodType.toLocaleLowerCase()}`,
                defaultMessage: `${capitalize(methodType.toLocaleLowerCase())}`,
            })}
        >
            {isDesktop ? (
                <ImageLogo className={style('logo')} srcPath={logoNavy} />
            ) : (
                <ImageLogo className={style('logo')} srcPath={logo} />
            )}
            <PaymentMethodSwitcher paymentMethod={provider} methodType={methodType} />
            <FormFlow
                className={style('form')}
                resetForm={formResetHandler}
                steps={stepData}
                page={'cashier'}
            />
        </Cashier>
    );
};
